import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { emailSchema, passwordSchema } from "@tabbio/utils";
import { AUTH_LINKED_ACCOUNTS_QUERY_KEY, useAuth } from "./useAuth";
import { checkEmailStatus } from "../lib/auth-api";
import { getAccountPasswordErrorMessage } from "../lib/account-password-error";
import {
  changeAccountPassword,
  confirmEmailChangeOtp,
  requestEmailChangeOtp,
  setAccountPassword,
  updateAccountName,
} from "../lib/account-actions";
import {
  useUsernameEditor,
  type InlineStatus,
  type TranslateFn,
} from "./useUsernameEditor";

export type { InlineStatus, TranslateFn } from "./useUsernameEditor";

function toText(
  t: TranslateFn,
  key: string,
  options?: Record<string, unknown>,
) {
  return String(t(key, options));
}

export function useAccountEditor(t: TranslateFn) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const usernameEditor = useUsernameEditor(t);
  // Account legal identity remains protected; the public username is handled
  // separately by useUsernameEditor and is intentionally editable.
  const isIdentityLocked = !!user?.isUaePassVerified;
  const identityLockMessage = usernameEditor.identityLockMessage;

  const userEmail = (user?.email ?? "").trim().toLowerCase();

  const [emailBase, setEmailBase] = useState<string>(userEmail);
  const [newEmail, setNewEmail] = useState<string>("");
  const [otp, setOtp] = useState<string>("");
  const [emailOtpRequested, setEmailOtpRequested] = useState(false);
  const [emailStatus, setEmailStatus] = useState<InlineStatus | null>(null);
  const [isRequestingOtp, setIsRequestingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  const [hasPassword, setHasPassword] = useState(true);
  const [isCheckingPasswordMode, setIsCheckingPasswordMode] = useState(false);
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [passwordStatus, setPasswordStatus] = useState<InlineStatus | null>(
    null,
  );
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  const [name, setName] = useState<string>(user?.name ?? "");
  const [nameStatus, setNameStatus] = useState<InlineStatus | null>(null);
  const [isSavingName, setIsSavingName] = useState(false);

  useEffect(() => {
    setName(user?.name ?? "");
  }, [user?.name]);

  useEffect(() => {
    setNameStatus(null);
  }, [name]);

  useEffect(() => {
    setEmailBase(userEmail);
  }, [userEmail]);

  useEffect(() => {
    setEmailStatus(null);
  }, [newEmail, otp]);

  useEffect(() => {
    setPasswordStatus(null);
  }, [currentPassword, newPassword, confirmPassword]);

  useEffect(() => {
    if (!emailBase) {
      setHasPassword(true);
      return;
    }

    let active = true;
    setIsCheckingPasswordMode(true);

    void checkEmailStatus(emailBase)
      .then((status) => {
        if (!active) return;
        setHasPassword(status.hasPassword);
      })
      .catch(() => {
        if (!active) return;
        setHasPassword(true);
      })
      .finally(() => {
        if (!active) return;
        setIsCheckingPasswordMode(false);
      });

    return () => {
      active = false;
    };
  }, [emailBase]);

  const requestEmailOtp = async () => {
    if (isIdentityLocked) {
      setEmailStatus({
        tone: "neutral",
        message: identityLockMessage,
      });
      return;
    }

    const normalizedNewEmail = newEmail.trim().toLowerCase();
    const parsed = emailSchema.safeParse(normalizedNewEmail);

    if (!parsed.success) {
      setEmailStatus({
        tone: "error",
        message: toText(t, "settings.accountEdit.emailInvalid"),
      });
      return;
    }

    if (normalizedNewEmail === emailBase) {
      setEmailStatus({
        tone: "error",
        message: toText(t, "settings.accountEdit.emailSameAsCurrent"),
      });
      return;
    }

    setIsRequestingOtp(true);
    setEmailStatus(null);
    try {
      await requestEmailChangeOtp(normalizedNewEmail);
      setEmailOtpRequested(true);
      setEmailStatus({
        tone: "success",
        message: toText(t, "settings.accountEdit.emailOtpSent"),
      });
    } catch (error) {
      setEmailStatus({
        tone: "error",
        message:
          error instanceof Error
            ? error.message
            : toText(t, "settings.accountEdit.errorGeneric"),
      });
    } finally {
      setIsRequestingOtp(false);
    }
  };

  const verifyEmailOtp = async () => {
    if (isIdentityLocked) {
      setEmailStatus({
        tone: "neutral",
        message: identityLockMessage,
      });
      return;
    }

    const normalizedNewEmail = newEmail.trim().toLowerCase();
    const parsed = emailSchema.safeParse(normalizedNewEmail);

    if (!parsed.success) {
      setEmailStatus({
        tone: "error",
        message: toText(t, "settings.accountEdit.emailInvalid"),
      });
      return;
    }

    if (!otp.trim()) {
      setEmailStatus({
        tone: "error",
        message: toText(t, "settings.accountEdit.otpRequired"),
      });
      return;
    }

    setIsVerifyingOtp(true);
    setEmailStatus(null);
    try {
      await confirmEmailChangeOtp(normalizedNewEmail, otp.trim());
      setEmailBase(normalizedNewEmail);
      setEmailOtpRequested(false);
      setNewEmail("");
      setOtp("");
      setEmailStatus({
        tone: "success",
        message: toText(t, "settings.accountEdit.emailUpdated"),
      });
    } catch (error) {
      setEmailStatus({
        tone: "error",
        message:
          error instanceof Error
            ? error.message
            : toText(t, "settings.accountEdit.errorGeneric"),
      });
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const savePassword = async () => {
    const parsed = passwordSchema.safeParse(newPassword);
    if (!parsed.success) {
      setPasswordStatus({
        tone: "error",
        message:
          parsed.error.errors[0]?.message ||
          toText(t, "settings.accountEdit.errorGeneric"),
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordStatus({
        tone: "error",
        message: toText(t, "settings.accountEdit.passwordMismatch"),
      });
      return;
    }

    if (hasPassword && !currentPassword.trim()) {
      setPasswordStatus({
        tone: "error",
        message: toText(t, "settings.accountEdit.currentPasswordRequired"),
      });
      return;
    }

    setIsSavingPassword(true);
    setPasswordStatus(null);
    try {
      if (hasPassword) {
        await changeAccountPassword(currentPassword, newPassword);
      } else {
        await setAccountPassword(newPassword);
        setHasPassword(true);
        await queryClient.invalidateQueries({
          queryKey: AUTH_LINKED_ACCOUNTS_QUERY_KEY,
        });
      }

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordStatus({
        tone: "success",
        message: hasPassword
          ? toText(t, "settings.accountEdit.passwordChanged")
          : toText(t, "settings.accountEdit.passwordSet"),
      });
    } catch (error) {
      setPasswordStatus({
        tone: "error",
        message: getAccountPasswordErrorMessage(error, t),
      });
    } finally {
      setIsSavingPassword(false);
    }
  };

  const saveName = async () => {
    if (isIdentityLocked) {
      setNameStatus({
        tone: "neutral",
        message: identityLockMessage,
      });
      return;
    }

    const trimmed = name.trim();
    if (!trimmed) {
      setNameStatus({
        tone: "error",
        message: toText(t, "settings.accountEdit.nameRequired"),
      });
      return;
    }

    setIsSavingName(true);
    setNameStatus(null);
    try {
      await updateAccountName(trimmed);
      setNameStatus({
        tone: "success",
        message: toText(t, "settings.accountEdit.nameUpdated"),
      });
    } catch (error) {
      setNameStatus({
        tone: "error",
        message:
          error instanceof Error
            ? error.message
            : toText(t, "settings.accountEdit.errorGeneric"),
      });
    } finally {
      setIsSavingName(false);
    }
  };

  return {
    displayName: user?.name || "",
    currentEmail: user?.email || emailBase,
    isIdentityLocked,
    identityLockMessage,

    // The username has its own lock (never UAE PASS-bound). Keep it separate
    // from isIdentityLocked so the name/email lock cannot leak onto it.
    isUsernameLocked: usernameEditor.isIdentityLocked,
    username: usernameEditor.username,
    setUsername: usernameEditor.setUsername,
    usernameChanged: usernameEditor.usernameChanged,
    currentUsername: usernameEditor.currentUsername,
    normalizedUsername: usernameEditor.normalizedUsername,
    usernameStatus: usernameEditor.usernameStatus,
    canSaveUsername: usernameEditor.canSaveUsername,
    saveUsername: usernameEditor.saveUsername,
    isSavingUsername: usernameEditor.isSavingUsername,

    newEmail,
    setNewEmail,
    otp,
    setOtp,
    emailOtpRequested,
    requestEmailOtp,
    verifyEmailOtp,
    emailStatus,
    isRequestingOtp,
    isVerifyingOtp,

    name,
    setName,
    nameStatus,
    saveName,
    isSavingName,

    hasPassword,
    isCheckingPasswordMode,
    currentPassword,
    setCurrentPassword,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    savePassword,
    passwordStatus,
    isSavingPassword,
  };
}
